import { memo, useEffect, useRef } from 'react'
import { useCountdown } from 'usehooks-ts'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useWebcam } from '../../../../hooks/useWebCam'

import { Heading } from '../Heading'
import { MediaPermissionsError } from '../MediaPermissionsError'

interface VideoRecorderProps {
  isRecording?: boolean
  userName: string
  recordingTime: number
  isMatchProcessing: boolean
  securityCode: string
  onRecordingStopped: () => void
  uploadValidationImage: (blob: Blob) => Promise<void>
  onVideoRecorded?: (blob: Blob) => void
  handleReFetchSecurityCode: () => void
}

const COUNTDOWN_INTERVAL = 1000
const INITIAL_COUNTDOWN = 4

const VideoRecorder: React.FC<VideoRecorderProps> = memo(
  ({
    isRecording: externalIsRecording,
    userName,
    recordingTime,
    isMatchProcessing,
    securityCode,
    onRecordingStopped,
    uploadValidationImage,
    onVideoRecorded,
    handleReFetchSecurityCode,
  }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const {
      ready,
      isRecording,
      startVideoRecording,
      stopVideoRecording,
      stopRecordingOnly,
      disconnectWebcam,
      takePhoto,
      hasWebCamPermission,
      hasMicrophonePermission,
    } = useWebcam(videoRef, true) // needs audio for consent

    const [count, { startCountdown, stopCountdown, resetCountdown }] =
      useCountdown({
        countStart: recordingTime,
        intervalMs: COUNTDOWN_INTERVAL,
      })
    const [
      initialCount,
      {
        startCountdown: startInitialCountdown,
        resetCountdown: resetInitialCountdown,
      },
    ] = useCountdown({
      countStart: INITIAL_COUNTDOWN,
      intervalMs: COUNTDOWN_INTERVAL,
    })

    const handleStartRecording = async () => {
      startVideoRecording(onVideoRecorded)
      startCountdown()
      const picture = await takePhoto()

      if (picture?.blob) {
        await uploadValidationImage(picture.blob)
      }
    }

    useEffect(() => {
      if (ready) {
        startInitialCountdown()
      }
    }, [ready])

    useEffect(() => {
      if (initialCount === 0) {
        void handleStartRecording()
      }
    }, [initialCount])

    useEffect(() => {
      if (externalIsRecording === false && isRecording) {
        stopVideoRecording()
      }
    }, [externalIsRecording, isRecording])

    useEffect(() => {
      if (count === 0 && isRecording) {
        // Add a buffer time to ensure MediaRecorder finishes processing the final chunk. The timeslice is 1000ms, so we need at least that much time
        setTimeout(() => {
          onRecordingStopped()
          stopCountdown()
        }, 1500)
      }
    }, [count, isRecording])

    const handleStopRecording = () => {
      onRecordingStopped()
      stopVideoRecording()
      disconnectWebcam()
    }

    const handleRestartRecording = () => {
      handleReFetchSecurityCode()
      if (isRecording) {
        stopRecordingOnly()
        stopCountdown()
        resetInitialCountdown()
      }

      resetCountdown()

      if (ready) {
        startInitialCountdown()
      }
    }

    // Show permission error if camera or microphone access is denied
    if (hasWebCamPermission === false || hasMicrophonePermission === false) {
      return (
        <MediaPermissionsError
          title="Camera & Microphone Access Required"
          description="We need access to your camera and microphone to record your verification video. Please enable both permissions in your browser settings to continue."
          instruction={[
            'Click the camera or microphone icon in your browser’s address bar',
            'Select Allow for camera and microphone',
            'Refresh the page if needed',
          ]}
        />
      )
    }

    return (
      <>
        {ready && (
          <Heading
            title="Verify Your Twin"
            subtitle="A 45-seconds video recording to prevent misuse and confirm the Digital
    Twin we’re creating is yours."
          />
        )}

        <div className="fixed z-[51] top-0 left-0 sm:relative rounded-2xl w-full h-full flex flex-col items-center sm:overflow-hidden">
          {ready ? (
            <>
              {/* Countdown overlay - centered over video */}

              {!!initialCount && initialCount <= 3 && (
                <div className="absolute inset-0 flex items-center justify-center z-50">
                  <div className="text-white text-center">
                    <div className="text-8xl font-bold">{initialCount}</div>
                  </div>
                </div>
              )}

              {/* Consent text overlay - only when not in countdown */}
              {!initialCount && (
                <>
                  <div className="absolute h-full z-50 top-0 left-0 flex flex-col">
                    <div className="relative p-6 z-10 w-full text-white text-center text-xl font-semibold leading-[120%] text-stroke-1 text-stroke-gray font-segoe">
                      My name is {userName}. By creating my digital twin, I can
                      create an AI version of myself that can talk to
                      colleagues, fans or clients while I work or sleep. My
                      digital twin belongs only to me, and I can manage or
                      delete it whenever I want. I give Napster permission to
                      use this recording of my voice to create my digital twin,
                      and this voice belongs to me. Napster has no rights to my
                      likeness or voice. My one-time verification code is:{' '}
                      {securityCode}
                    </div>
                    <div className="px-6 py-3 rounded-regular tc-mv-blur text-4xl text-white !border-0 sm:hidden block self-center">
                      00:{String(count).padStart(2, '0')}
                    </div>
                    <div className="absolute bottom-0 flex sm:hidden justify-center gap-4 left-1/2 transform -translate-x-1/2 pb-11">
                      <button
                        data-testid="video-recorder-restart-mobile-btn"
                        onClick={handleRestartRecording}
                        className="btn btn-secondary sm:!grow-0 !grow"
                        disabled={!isRecording}
                      >
                        <i className="fa-solid fa-rotate-right mr-2" />
                        Restart
                      </button>

                      <button
                        data-testid="video-recorder-finish-mobile-btn"
                        onClick={handleStopRecording}
                        className="btn btn-primary sm:!grow-0 !grow"
                        disabled={!isRecording || isMatchProcessing}
                      >
                        Finish Recording
                        <i className="fa-solid fa-arrow-right ml-2" />
                      </button>
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 z-50 sm:block hidden">
                    <div className="px-6 py-3 rounded-regular tc-mv-blur text-4xl text-white !border-0">
                      00:{String(count).padStart(2, '0')}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center z-50">
              <div className="text-white text-center">
                <AnimatedSpinner />
                <p>Connecting to camera...</p>
              </div>
            </div>
          )}

          <div
            className={`w-full h-full ${!ready ? 'opacity-0' : 'opacity-100 transition-opacity delay-100'}`}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              className="w-full h-full object-cover sm:rounded-2xl -scale-x-100"
              playsInline
              disablePictureInPicture
            />
            {/* Gradient background */}
            <div className="absolute inset-0 w-full h-82 bg-gradient-to-b from-black/40 to-transparent z-1" />
          </div>
        </div>
        <div className="hidden justify-center gap-4 mt-8 w-full sm:flex">
          <button
            data-testid="video-recorder-restart-desktop-btn"
            onClick={handleRestartRecording}
            className="btn btn-secondary sm:!grow-0 !grow"
            disabled={!isRecording}
          >
            <i className="fa-solid fa-rotate-right mr-2" />
            Restart
          </button>

          <button
            data-testid="video-recorder-finish-desktop-btn"
            onClick={handleStopRecording}
            className="btn btn-primary sm:!grow-0 !grow"
            disabled={!isRecording || isMatchProcessing}
          >
            Finish Recording
            <i className="fa-solid fa-arrow-right ml-2" />
          </button>
        </div>
      </>
    )
  },
)

export { VideoRecorder }
